import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { AGENTS_PATH } from '../../config';
import { cardSurfaceSx, interactiveCardSx, pressedCardSx, tokens } from '../../theme';
import type { AgentCardProps } from './types';

/**
 * An Agent Skill in the library's grid. The whole card is the link to its page, so the
 * methods it supports are **text and not links**: a card with several
 * destinations stays flat, and each of those methods is one click further on from
 * the Agent Skill's own page.
 *
 * **No stage colour.** Everything under the SDLC is tinted by lifecycle stage; an
 * Agent Skill can support methods in any of them, so it has no stage of its own and
 * borrowing one would claim a single stage the record does not carry.
 */

/** A neutral pill, drawn like a tool chip but not blue: blue is a tool, here. */
const Pill = ({ children }: { children: string }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: 'surface.sunken',
      borderRadius: `${String(tokens.radius.full)}px`,
      paddingInline: 3,
      paddingBlock: '3px',
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.4,
      color: 'text.secondary',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

export const AgentCard = ({ agent }: AgentCardProps) => (
  <RouterLink
    to={`${AGENTS_PATH}/${encodeURIComponent(agent.name)}`}
    style={{ textDecoration: 'none', display: 'flex', height: '100%' }}
  >
    <Box
      sx={{
        ...cardSurfaceSx,
        ...interactiveCardSx,
        ...pressedCardSx('border.strong'),
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        width: '100%',
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{ fontWeight: 500, letterSpacing: '1px', color: 'text.secondary' }}
      >
        {agent.kind === 'prompt' ? 'PROMPT' : 'AGENT SKILL'}
      </Typography>

      <Typography variant="h4" component="h3" sx={{ color: 'text.primary' }}>
        {agent.title}
      </Typography>

      {/* Clamped like the other cards: the whole summary is on the Agent Skill's page. */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
      >
        {agent.summary}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 'auto' }}>
        <Pill>{`v${agent.version}`}</Pill>
        {/* Absent rather than "1 version": one is the normal case and says nothing. */}
        {agent.versionCount > 1 && <Pill>{`${String(agent.versionCount)} versions`}</Pill>}
      </Box>

      {agent.methods.length > 0 && (
        <Typography variant="body2" sx={{ color: 'text.meta', fontSize: 12 }}>
          Supports {agent.methods.map((method) => method.name).join(', ')}
        </Typography>
      )}
    </Box>
  </RouterLink>
);
